import {
  DryFormulationUnitOfMeasure,
  LiquidFormulationUnitOfMeasure,
  parseDryFormulationUom,
  parseLiquidFormulationUom,
  parseRewardsUom,
} from '../enums';

export const LiquidScale = {
  [LiquidFormulationUnitOfMeasure.CENTILITRE]: 0.002641720523581484,
  [LiquidFormulationUnitOfMeasure.DECILITRE]: 0.02641720523581484,
  [LiquidFormulationUnitOfMeasure.GALLON]: 1,
  [LiquidFormulationUnitOfMeasure.FLUID_OUNCE]: 0.0078125,
  [LiquidFormulationUnitOfMeasure.KILOLITRE]: 264.1720523581484,
  [LiquidFormulationUnitOfMeasure.LITRE]: 0.2641720523581484,
  [LiquidFormulationUnitOfMeasure.MILLILITRE]: 0.00026417205235814843,
  [LiquidFormulationUnitOfMeasure.PINT]: 0.125,
  [LiquidFormulationUnitOfMeasure.QUART]: 0.25,
  [LiquidFormulationUnitOfMeasure.TOTE]: 275,
};

export const DryScale = {
  [DryFormulationUnitOfMeasure.GRAM]: 2.2046226218e-3,
  [DryFormulationUnitOfMeasure.KILOGRAM]: 2.2046226218,
  [DryFormulationUnitOfMeasure.POUND]: 1,
  [DryFormulationUnitOfMeasure.TON]: 2000,
  [DryFormulationUnitOfMeasure.OUNCE]: 0.0625,
};

export class ConversionError extends Error {
}

export class ConversionUtility {
  static convertQuantity (
    quantity: number,
    sourceUoM: string | null,
    targetUoM: string | null,
  ): number {
    if (!sourceUoM || !targetUoM || quantity === 0) {
      return 0;
    }

    if (parseRewardsUom(sourceUoM) && parseRewardsUom(sourceUoM) === parseRewardsUom(targetUoM)) {
      return quantity;
    }

    const drySource = parseDryFormulationUom(sourceUoM);
    const dryTarget = parseDryFormulationUom(targetUoM);
    if (drySource && dryTarget) {
      return this.convertDryQuantity(quantity, drySource, dryTarget);
    }

    const liquidSource = parseLiquidFormulationUom(sourceUoM);
    const liquidTarget = parseLiquidFormulationUom(targetUoM);
    if (liquidSource && liquidTarget) {
      return this.convertLiquidQuantity(quantity, liquidSource, liquidTarget);
    }

    throw new ConversionError(
      `No conversion found for source "${sourceUoM}" and target "${targetUoM}"`,
    );
  }

  static areAllConvertible (sourceUoMs: string[], targetUom: string | null) {
    return sourceUoMs.length > 0 && sourceUoMs.every(
      uom => this.isConvertible(uom, targetUom),
    );
  }

  static isAnyConvertible (sourceUoMs: string[], targetUom: string | null) {
    return sourceUoMs.some(
      uom => this.isConvertible(uom, targetUom),
    );
  }

  static isConvertible (sourceUoM: string | null, targetUoM: string | null) {
    if (!sourceUoM || !targetUoM) {
      return false;
    } else if (sourceUoM === targetUoM) {
      return true;
    } else if (
      parseRewardsUom(sourceUoM) && parseRewardsUom(sourceUoM) === parseRewardsUom(targetUoM)
    ) {
      return true;
    } else if (parseLiquidFormulationUom(sourceUoM) && parseLiquidFormulationUom(targetUoM)) {
      return true;
    }
    return !!parseDryFormulationUom(sourceUoM) && !!parseDryFormulationUom(targetUoM);
  }

  private static convertLiquidQuantity (
    quantity: number,
    sourceUoM: LiquidFormulationUnitOfMeasure,
    targetUoM: LiquidFormulationUnitOfMeasure,
  ): number {
    return (quantity * LiquidScale[sourceUoM]) / LiquidScale[targetUoM];
  }

  private static convertDryQuantity (
    quantity: number,
    sourceUoM: DryFormulationUnitOfMeasure,
    targetUoM: DryFormulationUnitOfMeasure,
  ): number {
    return (quantity * DryScale[sourceUoM]) / DryScale[targetUoM];
  }
}
